import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Card } from '@ovComponents/2-component/card/card';
import {
  DataTable,
  type DataTableTanstackTable,
  type Row,
  type RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import {
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReconFlagRow } from '../utils/reconFlags.types';

export type ReconFlagsGridCardProps = {
  sys: Tokens['sys'];
  table: DataTableTanstackTable<ReconFlagRow>;
  pageSize: RowsPerPageOption;
  tableLoading: boolean;
  pageCount: number;
  page: number;
  onPageChange: (page: number) => void;
  totalRowCount: number;
  onPerPageChange: (perPage: number) => void;
  onRowClick: (row: ReconFlagRow) => void;
  selectedCount: number;
  onSelectAllOnPage: () => void;
  onClearSelection: () => void;
  onBulkDismiss: () => boolean | Promise<boolean>;
  bulkDismissLoading: boolean;
  gridQueryLoading: boolean;
};

export const ReconFlagsGridCard = ({
  sys,
  table,
  pageSize,
  tableLoading,
  pageCount,
  page,
  onPageChange,
  totalRowCount,
  onPerPageChange,
  onRowClick,
  selectedCount,
  onSelectAllOnPage,
  onClearSelection,
  onBulkDismiss,
  bulkDismissLoading,
  gridQueryLoading,
}: ReconFlagsGridCardProps) => {
  const { t } = useTranslation(['reconciliation']);
  const [bulkDismissConfirmOpen, setBulkDismissConfirmOpen] = useState(false);

  useEffect(() => {
    if (selectedCount === 0) {
      setBulkDismissConfirmOpen(false);
    }
  }, [selectedCount]);

  const handleConfirmBulkDismiss = (): void => {
    void (async () => {
      const succeeded = await onBulkDismiss();
      if (succeeded) {
        setBulkDismissConfirmOpen(false);
      }
    })();
  };

  const showSelectAllOnPage = table
    .getRowModel()
    .rows.some((row) => row.getCanSelect() && !row.getIsSelected());

  return (
    <Box display='flex' flexDirection='column' gap={1.5}>
      {selectedCount > 0 && (
        <Card
          variant='outlined'
          sx={{
            backgroundColor: sys.color.primaryVariant,
            borderColor: sys.color.primaryVariantPressed,
            boxShadow: 'none',
          }}
        >
          <Box
            sx={{
              py: 1,
              pr: 1,
              pl: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              variant='bodyMedium'
              sx={{ color: sys.color.onSurface, fontWeight: 600 }}
            >
              {t('reconFlags.selection.count', { count: selectedCount })}
            </Typography>
            <Box display='flex' alignItems='center' flexWrap='wrap' gap={1}>
              {showSelectAllOnPage && (
                <Button
                  variant='outlined'
                  color='primary'
                  size='sm'
                  label={t('reconFlags.selection.selectAllOnPage')}
                  onClick={onSelectAllOnPage}
                  disabled={bulkDismissLoading}
                />
              )}
              <Button
                variant='outlined'
                color='primary'
                size='sm'
                label={t('reconFlags.selection.clear')}
                onClick={onClearSelection}
                disabled={bulkDismissLoading}
              />
              <Button
                variant='filled'
                color='primary'
                size='sm'
                label={t('reconFlags.selection.dismiss', {
                  count: selectedCount,
                })}
                loading={bulkDismissLoading}
                disabled={bulkDismissLoading}
                onClick={() => setBulkDismissConfirmOpen(true)}
              />
            </Box>
          </Box>
        </Card>
      )}

      <ConfirmationModal
        open={bulkDismissConfirmOpen}
        title={t('reconFlags.selection.dismissConfirmTitle', {
          count: selectedCount,
        })}
        bodyText={t('reconFlags.selection.dismissConfirmBody', {
          count: selectedCount,
        })}
        confirmButtonLabel={t('reconFlags.selection.dismissConfirmAction', {
          count: selectedCount,
        })}
        confirmButtonColor={ConfirmButtonColors.primary}
        loading={bulkDismissLoading}
        onConfirm={handleConfirmBulkDismiss}
        onCancel={() => setBulkDismissConfirmOpen(false)}
        testIdPrefix='recon-flags-bulk-dismiss'
      />

      <Card loading={gridQueryLoading}>
        <Box sx={{ overflowX: 'auto' }}>
          <DataTable
            table={table}
            rowsPerPage={pageSize}
            loading={tableLoading}
            emptyContent={
              <Typography variant='bodyMedium' sx={{ py: 2 }}>
                {t('reconFlags.emptyTable')}
              </Typography>
            }
            getRowProps={(row: Row<ReconFlagRow>) => ({
              hover: true,
              pointer: true,
              onClick: () => onRowClick(row.original),
            })}
            tablePagination={{
              mode: 'controlled',
              ovVariant: 'tableFooter',
              pageCount,
              page,
              onPageChange,
              totalRowCount,
              perPage: pageSize,
              onPerPageChange,
              showPerPageSelect: true,
              summaryNamespace: 'reconciliation',
              summaryKey: 'reconFlags.pagination.summary',
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};
